// src/api.js
// ตัวช่วยเรียก REST API หลักของระบบ Beauty Salon v2.0.1
// พร้อมระบบ In-Memory Cache (SWR) และ Request Deduplication เพื่อลดการใช้ Cloudflare Worker CPU

import axios from "axios";

// Local development defaults to Flask on localhost. Production builds default to
// the Cloudflare Worker API URL if not explicitly configured in the environment.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://127.0.0.1:5000/api"
    : "https://pos88-api.kukkik.workers.dev/api");

if (!API_BASE_URL) {
  throw new Error("REACT_APP_API_BASE_URL must be configured");
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// ==========================================
// 1. In-Memory Client Cache & Dedup System
// ==========================================
const apiCache = new Map();
const inFlightRequests = new Map();

// กำหนดระยะเวลาแคช (TTL) สำหรับแต่ละประเภท Endpoint
const TTL_MASTER = 300000;    // 5 นาที: ข้อมูลที่ไม่ค่อยเปลี่ยน (พนักงาน, หมวดหมู่บริการ, หมวดหมู่ค่าใช้จ่าย, การตั้งค่า)
const TTL_CATALOG = 120000;   // 2 นาที: รายการบริการ, คูปองส่วนลด, ลูกค้า
const TTL_DASHBOARD = 45000;  // 45 วินาที: ยอดสรุป Dashboard และกราฟรายงานต่างๆ
const TTL_SHORT = 10000;      // 10 วินาที: รายการทั่วไป (บิล, ค่าใช้จ่าย, นัดหมาย) ป้องกันการคลิกสลับหน้าไปมารัวๆ

function getEndpointTtl(url = "") {
  const cleanUrl = url.toLowerCase();
  
  // Endpoint ที่ห้ามแคชเด็ดขาด (Realtime)
  if (cleanUrl.includes("/reminders/due")) return 0;
  
  // Master data: พนักงาน, หมวดหมู่, การตั้งค่า, เวอร์ชัน
  if (
    cleanUrl.includes("/employees") ||
    cleanUrl.includes("/service-categories") ||
    cleanUrl.includes("/expense-categories") ||
    cleanUrl.includes("/settings") ||
    cleanUrl.includes("/version")
  ) {
    return TTL_MASTER;
  }

  // Catalog: บริการ, ส่วนลด, ลูกค้า
  if (
    cleanUrl.includes("/services") ||
    cleanUrl.includes("/discount-codes") ||
    cleanUrl.includes("/customers")
  ) {
    return TTL_CATALOG;
  }

  // Dashboard & Reports
  if (
    cleanUrl.includes("/dashboard") ||
    cleanUrl.includes("/reports")
  ) {
    return TTL_DASHBOARD;
  }

  // อื่นๆ ที่เป็น GET
  return TTL_SHORT;
}

// สร้างคีย์เฉพาะสำหรับแคช
function buildCacheKey(url, config = {}) {
  const paramsStr = config.params ? JSON.stringify(config.params) : "";
  return `${url}?${paramsStr}`;
}

// ล้างแคชที่ตรงกับ Pattern
export const clearApiCache = (pattern) => {
  if (!pattern) {
    apiCache.clear();
    return;
  }
  const regex = typeof pattern === "string" ? new RegExp(pattern, "i") : pattern;
  for (const key of apiCache.keys()) {
    if (regex.test(key)) {
      apiCache.delete(key);
    }
  }
};

// ล้างแคชอัตโนมัติเมื่อมีการแก้ไขข้อมูล (Mutation: POST, PUT, DELETE, PATCH)
function invalidateCacheOnMutation(url = "") {
  const cleanUrl = url.toLowerCase();
  if (cleanUrl.includes("/employees")) {
    clearApiCache("/employees");
  }
  if (cleanUrl.includes("/services") || cleanUrl.includes("/service-categories")) {
    clearApiCache("/services");
    clearApiCache("/service-categories");
  }
  if (cleanUrl.includes("/expenses") || cleanUrl.includes("/expense-categories")) {
    clearApiCache("/expenses");
    clearApiCache("/expense-categories");
    clearApiCache("/dashboard");
    clearApiCache("/reports");
    clearApiCache("/cashflow");
  }
  if (cleanUrl.includes("/orders")) {
    clearApiCache("/orders");
    clearApiCache("/dashboard");
    clearApiCache("/reports");
    clearApiCache("/cashflow");
    clearApiCache("/commissions");
  }
  if (cleanUrl.includes("/bookings") || cleanUrl.includes("/appointments")) {
    clearApiCache("/bookings");
    clearApiCache("/appointments");
    clearApiCache("/dashboard");
  }
  if (cleanUrl.includes("/customers") || cleanUrl.includes("/deposits")) {
    clearApiCache("/customers");
    clearApiCache("/deposits");
  }
  if (cleanUrl.includes("/discount-codes")) {
    clearApiCache("/discount-codes");
  }
  if (cleanUrl.includes("/settings")) {
    clearApiCache("/settings");
  }
  if (cleanUrl.includes("/deductions") || cleanUrl.includes("/commissions")) {
    clearApiCache("/deductions");
    clearApiCache("/commissions");
  }
}

// Clone Response อย่างปลอดภัยเพื่อป้องกันปัญหา Object Mutation ใน React
function cloneResponse(res) {
  let clonedData = res.data;
  if (Array.isArray(res.data)) {
    clonedData = [...res.data];
  } else if (typeof res.data === "object" && res.data !== null) {
    clonedData = { ...res.data };
  }
  return {
    ...res,
    data: clonedData,
    fromCache: true,
  };
}

// ==========================================
// 2. Safe GET Concurrency Limiter (Auto-releasing)
// ==========================================
// ควบคุมให้มี GET request พร้อมกันไม่เกิน 3 คำขอ เพื่อประหยัด CPU ของ Worker
// และมี Safety Timeout 4 วินาที ป้องกัน Deadlock 100%
// สำหรับคำขอ Mutation (POST/PUT/DELETE) จะทำงานทันทีเสมอ ไม่ต้องรอคิว
let activeGetRequests = 0;
const MAX_CONCURRENT_GET = 3;
const getQueue = [];

function acquireGetSlot() {
  if (activeGetRequests < MAX_CONCURRENT_GET) {
    activeGetRequests++;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      const idx = getQueue.indexOf(run);
      if (idx !== -1) getQueue.splice(idx, 1);
      resolve();
    }, 4000);

    const run = () => {
      clearTimeout(timer);
      resolve();
    };
    getQueue.push(run);
  });
}

function releaseGetSlot() {
  if (getQueue.length > 0) {
    const next = getQueue.shift();
    next();
  } else {
    activeGetRequests = Math.max(0, activeGetRequests - 1);
  }
}

// ใส่ JWT token จาก localStorage ให้ทุก request อัตโนมัติ
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ถ้า token หมดอายุ / ไม่ถูกต้อง → เคลียร์ข้อมูลแล้วเด้งไปหน้า login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.status === 401 || error.response.status === 422) {
        // เคลียร์ข้อมูลผู้ใช้เมื่อ token หมดอายุ หรือ ไม่ตรงกับ secret key ของ backend
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        // กัน redirect loop ถ้าอยู่หน้า login อยู่แล้ว
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    } else {
      // กรณี network ล่ม หรือ backend ไม่ตอบ
      console.error("Network or CORS error:", error);
    }

    return Promise.reject(error);
  }
);

// ==========================================
// 3. Method Interception for Cache & Dedup
// ==========================================
const rawGet = api.get.bind(api);
const rawPost = api.post.bind(api);
const rawPut = api.put.bind(api);
const rawDelete = api.delete.bind(api);
const rawPatch = api.patch.bind(api);

api.get = async function (url, config = {}) {
  const ttl = config.skipCache ? 0 : getEndpointTtl(url);
  const cacheKey = buildCacheKey(url, config);

  // 1. ตรวจสอบ In-Memory Cache (ตอบกลับทันทีใน 0ms ไม่ต้องรอคิวและไม่กิน CPU)
  if (ttl > 0 && apiCache.has(cacheKey)) {
    const entry = apiCache.get(cacheKey);
    if (Date.now() < entry.expiresAt) {
      return Promise.resolve(cloneResponse(entry.response));
    }
    apiCache.delete(cacheKey);
  }

  // 2. Request Deduplication: หากมีคำขอนี้กำลังทำงานอยู่ ให้แชร์ผลลัพธ์ร่วมกัน
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey).then(cloneResponse);
  }

  // 3. ส่งคำขอจริงผ่าน Concurrency Slot ป้องกัน Server โดนถล่มพร้อมกัน
  await acquireGetSlot();
  const promise = rawGet(url, config)
    .then((res) => {
      if (ttl > 0 && res.status >= 200 && res.status < 300) {
        apiCache.set(cacheKey, {
          response: res,
          expiresAt: Date.now() + ttl,
        });
      }
      return res;
    })
    .finally(() => {
      releaseGetSlot();
      inFlightRequests.delete(cacheKey);
    });

  inFlightRequests.set(cacheKey, promise);
  return promise;
};

// Mutations: เมื่อมีการบันทึก/แก้ไข/ลบ จะทำการเคลียร์แคชที่เกี่ยวข้องทันที
api.post = function (url, data, config) {
  return rawPost(url, data, config).then((res) => {
    invalidateCacheOnMutation(url);
    return res;
  });
};

api.put = function (url, data, config) {
  return rawPut(url, data, config).then((res) => {
    invalidateCacheOnMutation(url);
    return res;
  });
};

api.delete = function (url, config) {
  return rawDelete(url, config).then((res) => {
    invalidateCacheOnMutation(url);
    return res;
  });
};

api.patch = function (url, data, config) {
  return rawPatch(url, data, config).then((res) => {
    invalidateCacheOnMutation(url);
    return res;
  });
};

// ==========================================
// 4. Convenience APIs
// ==========================================
// Customer API
export const getCustomers = (config) => api.get("/customers", config);
export const createCustomer = (data) => api.post("/customers", data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const getCustomerHistory = (id, config) => api.get(`/customers/${id}/history`, config);

// Discount Code API
export const listDiscountCodes = (config) => api.get("/discount-codes", config);
export const createDiscountCode = (data) => api.post("/discount-codes", data);
export const updateDiscountCode = (id, data) => api.put(`/discount-codes/${id}`, data);
export const deleteDiscountCode = (id) => api.delete(`/discount-codes/${id}`);
export const validateDiscountCode = (data) => api.post("/discount-codes/validate", data);

export default api;
